import React, { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import { IconClock, IconMapPin, IconDeviceMobile, IconWifi, IconCalendar, IconPlus, IconTrash } from "@tabler/icons-react";
import { fetchHrAttendanceSettings, updateHrAttendanceSettings } from "../../../../apis/hrApi";
import ToggleButton from "../../../../components/ToggleButton";

export default function AttendanceTab() {
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Core State variables matching postman fields
  const [allowShiftChangeRequest, setAllowShiftChangeRequest] = useState(true);
  const [allowSelfClockInOut, setAllowSelfClockInOut] = useState(true);
  const [allowClockInOutsideShift, setAllowClockInOutsideShift] = useState(false);
  const [enableEndOfDay, setEnableEndOfDay] = useState(false);

  // Auto Clock In
  const [enableAutoClockIn, setEnableAutoClockIn] = useState(true);
  const [autoClockInMinutesBefore, setAutoClockInMinutesBefore] = useState(15);
  const [enableAutoClockOut, setEnableAutoClockOut] = useState(true);
  const [autoClockOutMinutesAfter, setAutoClockOutMinutesAfter] = useState(30);

  // Location Restriction
  const [enableLocationRestriction, setEnableLocationRestriction] = useState(true);
  const [latitude, setLatitude] = useState(28.613939);
  const [longitude, setLongitude] = useState(77.209021);
  const [radiusInMeters, setRadiusInMeters] = useState(100);

  // Device Restriction
  const [enableDeviceRestriction, setEnableDeviceRestriction] = useState(true);
  const [maximumDevices, setMaximumDevices] = useState(2);

  // IP Restriction
  const [enableIpRestriction, setEnableIpRestriction] = useState(true);
  const [maximumIpAddresses, setMaximumIpAddresses] = useState(2);
  const [allowedIpAddresses, setAllowedIpAddresses] = useState<{ ipAddress: string; label: string }[]>([
    { ipAddress: "103.25.45.101", label: "Head Office" },
    { ipAddress: "103.25.45.102", label: "Branch Office" }
  ]);

  // WiFi Restriction
  const [enableWifiRestriction, setEnableWifiRestriction] = useState(true);
  const [wifiNetworks, setWifiNetworks] = useState<{ bssid: string; ssid: string }[]>([
    { bssid: "90:6C:AC:71:54:FB", ssid: "Head Office WiFi" },
    { bssid: "A8:5E:45:12:78:9C", ssid: "Warehouse WiFi" }
  ]);

  // Schedule & Reminders
  const [weekStartsFrom, setWeekStartsFrom] = useState("MONDAY");
  const [enableAttendanceReminder, setEnableAttendanceReminder] = useState(true);
  const [attendanceReminderMinutesBefore, setAttendanceReminderMinutesBefore] = useState(15);

  // Temp form fields for adding to list
  const [newIpAddress, setNewIpAddress] = useState("");
  const [newIpLabel, setNewIpLabel] = useState("");
  
  const [newWifiSsid, setNewWifiSsid] = useState("");
  const [newWifiBssid, setNewWifiBssid] = useState("");

  // Load Settings from Backend
  const loadSettings = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await fetchHrAttendanceSettings();
      if (res.success && res.data) {
        let data = res.data;
        if (res.data && typeof res.data === "object") {
          if (Array.isArray(res.data)) {
            data = res.data[0];
          } else if (res.data.settings) {
            data = res.data.settings;
          } else if (Array.isArray(res.data.list)) {
            data = res.data.list[0];
          } else if (res.data.data) {
            if (Array.isArray(res.data.data)) {
              data = res.data.data[0];
            } else {
              data = res.data.data;
            }
          }
        }
        
        if (data) {
          setAllowShiftChangeRequest(data.allowShiftChangeRequest ?? true);
          setAllowSelfClockInOut(data.allowSelfClockInOut ?? true);
          setAllowClockInOutsideShift(data.allowClockInOutsideShift ?? false);
          setEnableEndOfDay(data.enableEndOfDay ?? false);

          setEnableAutoClockIn(data.enableAutoClockIn ?? true);
          setAutoClockInMinutesBefore(data.autoClockInMinutesBefore ?? 15);
          setEnableAutoClockOut(data.enableAutoClockOut ?? true);
          setAutoClockOutMinutesAfter(data.autoClockOutMinutesAfter ?? 30);

          setEnableLocationRestriction(data.enableLocationRestriction ?? true);
          if (data.officeLocation) {
            setLatitude(data.officeLocation.latitude ?? 28.613939);
            setLongitude(data.officeLocation.longitude ?? 77.209021);
            setRadiusInMeters(data.officeLocation.radiusInMeters ?? 100);
          }

          setEnableDeviceRestriction(data.enableDeviceRestriction ?? true);
          setMaximumDevices(data.maximumDevices ?? 2);

          setEnableIpRestriction(data.enableIpRestriction ?? true);
          setMaximumIpAddresses(data.maximumIpAddresses ?? 2);
          if (Array.isArray(data.allowedIpAddresses)) {
            setAllowedIpAddresses(data.allowedIpAddresses);
          }

          setEnableWifiRestriction(data.enableWifiRestriction ?? true);
          if (Array.isArray(data.wifiNetworks)) {
            setWifiNetworks(data.wifiNetworks);
          }

          setWeekStartsFrom(data.weekStartsFrom ?? "MONDAY");
          setEnableAttendanceReminder(data.enableAttendanceReminder ?? true);
          setAttendanceReminderMinutesBefore(data.attendanceReminderMinutesBefore ?? 15);
        }
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load attendance settings");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Handle Save
  const handleSave = async () => {
    setIsSaving(true);
    try {
      const payload = {
        allowShiftChangeRequest,
        allowSelfClockInOut,
        allowClockInOutsideShift,
        enableEndOfDay,
        enableAutoClockIn,
        autoClockInMinutesBefore: Number(autoClockInMinutesBefore),
        enableAutoClockOut,
        autoClockOutMinutesAfter: Number(autoClockOutMinutesAfter),
        enableLocationRestriction,
        officeLocation: {
          latitude: Number(latitude),
          longitude: Number(longitude),
          radiusInMeters: Number(radiusInMeters)
        },
        enableDeviceRestriction,
        maximumDevices: Number(maximumDevices),
        enableIpRestriction,
        maximumIpAddresses: Number(maximumIpAddresses),
        allowedIpAddresses,
        enableWifiRestriction,
        wifiNetworks,
        weekStartsFrom,
        enableAttendanceReminder,
        attendanceReminderMinutesBefore: Number(attendanceReminderMinutesBefore)
      };

      const res = await updateHrAttendanceSettings(payload);
      if (res.success) {
        toast.success("Attendance settings saved successfully!");
        loadSettings();
      } else {
        toast.error(res.message || "Failed to save settings");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error saving attendance settings");
    } finally {
      setIsSaving(false);
    }
  };

  // Add Allowed IP
  const addIpAddress = () => {
    if (!newIpAddress.trim()) {
      toast.error("IP Address is required");
      return;
    }
    const cleanIp = newIpAddress.trim();
    if (allowedIpAddresses.some(item => item.ipAddress === cleanIp)) {
      toast.error("IP Address already added");
      return;
    }
    setAllowedIpAddresses([...allowedIpAddresses, { ipAddress: cleanIp, label: newIpLabel.trim() || "IP Address" }]);
    setNewIpAddress("");
    setNewIpLabel("");
  };

  // Delete Allowed IP
  const removeIpAddress = (index: number) => {
    setAllowedIpAddresses(allowedIpAddresses.filter((_, idx) => idx !== index));
  };

  // Add WiFi Network
  const addWifiNetwork = () => {
    if (!newWifiSsid.trim()) {
      toast.error("WiFi Name/SSID is required");
      return;
    }
    const cleanBssid = newWifiBssid.trim();
    if (cleanBssid && wifiNetworks.some(item => item.bssid.toLowerCase() === cleanBssid.toLowerCase())) {
      toast.error("BSSID/MAC Address already added");
      return;
    }
    setWifiNetworks([...wifiNetworks, { ssid: newWifiSsid.trim(), bssid: cleanBssid }]);
    setNewWifiSsid("");
    setNewWifiBssid("");
  };

  // Remove WiFi Network
  const removeWifiNetwork = (index: number) => {
    setWifiNetworks(wifiNetworks.filter((_, idx) => idx !== index));
  };

  return (
    <div className="space-y-6 border border-[#d2d2d2] rounded-[20px] p-6 bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5] mb-12 mx-auto max-w-5xl mt-2 select-none">
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-xl font-semibold text-[#212121]">Attendance Settings</h2>
        {isLoading && (
          <span className="text-xs text-gray-400 font-medium animate-pulse">Loading...</span>
        )}
      </div>

      <div className="bg-[#FFFFFF] p-6 rounded-[20px] space-y-8 border border-[#D2D2D2]">
        
        {/* Basic Settings */}
        <div className="border border-slate-100 rounded-xl p-5 bg-[#FFFFFD]">
          <h3 className="text-sm font-semibold text-[#212121] mb-4 flex items-center gap-2">
            <IconClock size={18} className="text-[#FBD037]" />
            Basic Settings
          </h3>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="allowShiftChangeRequest"
                checked={allowShiftChangeRequest}
                onChange={(e) => setAllowShiftChangeRequest(e.target.checked)}
                className="accent-[#FBD037] w-4.5 h-4.5 rounded cursor-pointer"
              />
              <label htmlFor="allowShiftChangeRequest" className="text-[#1E293B] text-xs font-semibold cursor-pointer">
                Allow Employee Shift Change Requests
              </label>
            </div>
            
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="allowSelfClockInOut"
                checked={allowSelfClockInOut}
                onChange={(e) => setAllowSelfClockInOut(e.target.checked)}
                className="accent-[#FBD037] w-4.5 h-4.5 rounded cursor-pointer"
              />
              <label htmlFor="allowSelfClockInOut" className="text-[#1E293B] text-xs font-semibold cursor-pointer">
                Self Clock-in/Clock-out
              </label>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="allowClockInOutsideShift"
                checked={allowClockInOutsideShift}
                onChange={(e) => setAllowClockInOutsideShift(e.target.checked)}
                className="accent-[#FBD037] w-4.5 h-4.5 rounded cursor-pointer"
              />
              <label htmlFor="allowClockInOutsideShift" className="text-[#1E293B] text-xs font-semibold cursor-pointer">
                Clock-In Outside Shift Hours
              </label>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="enableEndOfDay"
                checked={enableEndOfDay}
                onChange={(e) => setEnableEndOfDay(e.target.checked)}
                className="accent-[#FBD037] w-4.5 h-4.5 rounded cursor-pointer"
              />
              <label htmlFor="enableEndOfDay" className="text-[#1E293B] text-xs font-semibold cursor-pointer">
                Enable End of Day (EOD)
              </label>
            </div>
          </div>
        </div>

        {/* Auto Clock-in */}
        <div className="border border-slate-100 rounded-xl p-5 bg-[#FFFFFD]">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-sm font-semibold text-[#212121] flex items-center gap-2">
              <IconClock size={18} className="text-[#FBD037]" />
              Auto Clock-in
            </h3>
            <ToggleButton
              checked={enableAutoClockIn}
              onChange={(e) => setEnableAutoClockIn(e.target.checked)}
            />
          </div>
          {enableAutoClockIn && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2 animate-in fade-in duration-200">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                  Auto Clock-in Minutes Before Shift Start
                </label>
                <input
                  type="number"
                  value={autoClockInMinutesBefore}
                  onChange={(e) => setAutoClockInMinutesBefore(parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-[#FBD037] text-sm font-medium"
                />
              </div>
              <div>
                <div className="flex justify-between items-center mb-1.5">
                  <label className="block text-xs font-semibold text-gray-600">
                    Enable Auto Clock-out
                  </label>
                  <ToggleButton
                    checked={enableAutoClockOut}
                    onChange={(e) => setEnableAutoClockOut(e.target.checked)}
                  />
                </div>
                {enableAutoClockOut && (
                  <div className="animate-in fade-in duration-200">
                    <label className="block text-xs font-semibold text-gray-400 mb-1.5">
                      Auto Clock-out Minutes After Shift End
                    </label>
                    <input
                      type="number"
                      value={autoClockOutMinutesAfter}
                      onChange={(e) => setAutoClockOutMinutesAfter(parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-[#FBD037] text-sm font-medium"
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Location Restrictions */}
        <div className="border border-slate-100 rounded-xl p-5 bg-[#FFFFFD]">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-sm font-semibold text-[#212121] flex items-center gap-2">
              <IconMapPin size={18} className="text-[#FBD037]" />
              Location Restrictions
            </h3>
            <ToggleButton
              checked={enableLocationRestriction}
              onChange={(e) => setEnableLocationRestriction(e.target.checked)}
            />
          </div>
          {enableLocationRestriction && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2 animate-in fade-in duration-200">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                  Office Location Latitude
                </label>
                <input
                  type="number"
                  step="any"
                  value={latitude}
                  onChange={(e) => setLatitude(parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-[#FBD037] text-sm font-medium"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                  Office Location Longitude
                </label>
                <input
                  type="number"
                  step="any"
                  value={longitude}
                  onChange={(e) => setLongitude(parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-[#FBD037] text-sm font-medium"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                  Radius (in Meters)
                </label>
                <input
                  type="number"
                  value={radiusInMeters}
                  onChange={(e) => setRadiusInMeters(parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-[#FBD037] text-sm font-medium"
                />
              </div>
            </div>
          )}
        </div>

        {/* Device & Network Restrictions */}
        <div className="border border-slate-100 rounded-xl p-5 bg-[#FFFFFD]">
          <h3 className="text-sm font-semibold text-[#212121] mb-6 flex items-center gap-2">
            <IconDeviceMobile size={18} className="text-[#FBD037]" />
            Device & Network Restrictions
          </h3>
          
          <div className="space-y-6">
            {/* Device restriction config */}
            <div className="flex flex-col gap-3 pb-4 border-b border-slate-100">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="enableDeviceRestriction"
                    checked={enableDeviceRestriction}
                    onChange={(e) => setEnableDeviceRestriction(e.target.checked)}
                    className="accent-[#FBD037] w-4.5 h-4.5 rounded cursor-pointer"
                  />
                  <label htmlFor="enableDeviceRestriction" className="text-[#1E293B] text-xs font-semibold cursor-pointer">
                    Device Based Clockin
                  </label>
                </div>
              </div>
              {enableDeviceRestriction && (
                <div className="max-w-xs animate-in fade-in duration-200">
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                    Max Devices Allowed
                  </label>
                  <input
                    type="number"
                    value={maximumDevices}
                    onChange={(e) => setMaximumDevices(parseInt(e.target.value) || 1)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-[#FBD037] text-sm font-medium"
                  />
                </div>
              )}
            </div>

            {/* IP restriction config */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="enableIpRestriction"
                    checked={enableIpRestriction}
                    onChange={(e) => setEnableIpRestriction(e.target.checked)}
                    className="accent-[#FBD037] w-4.5 h-4.5 rounded cursor-pointer"
                  />
                  <label htmlFor="enableIpRestriction" className="text-[#1E293B] text-xs font-semibold cursor-pointer">
                    IP Address Restriction
                  </label>
                </div>
              </div>

              {enableIpRestriction && (
                <div className="space-y-4 animate-in fade-in duration-200">
                  <div className="max-w-xs">
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                      Max IP Addresses Allowed
                    </label>
                    <input
                      type="number"
                      value={maximumIpAddresses}
                      onChange={(e) => setMaximumIpAddresses(parseInt(e.target.value) || 1)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-[#FBD037] text-sm font-medium"
                    />
                  </div>

                  {/* Allowed IP list */}
                  <div className="border border-slate-100 rounded-lg overflow-hidden">
                    <table className="w-full text-left border-collapse text-xs">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-100 font-semibold text-slate-700">
                          <th className="p-3">IP Address</th>
                          <th className="p-3">Label / Name</th>
                          <th className="p-3 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {allowedIpAddresses.map((ipItem, index) => (
                          <tr key={index} className="hover:bg-slate-50/50">
                            <td className="p-3 font-medium text-slate-800">{ipItem.ipAddress}</td>
                            <td className="p-3 text-slate-600">{ipItem.label}</td>
                            <td className="p-3 text-right">
                              <button
                                type="button"
                                onClick={() => removeIpAddress(index)}
                                className="text-red-500 hover:text-red-700 p-1.5 rounded-lg hover:bg-red-50 transition active:scale-95"
                              >
                                <IconTrash size={16} />
                              </button>
                            </td>
                          </tr>
                        ))}
                        {allowedIpAddresses.length === 0 && (
                          <tr>
                            <td colSpan={3} className="p-4 text-center text-slate-400 italic">No IP restrictions configured.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Form to add IP address */}
                  <div className="bg-slate-50/50 p-4 rounded-xl border border-slate-100 flex flex-col md:flex-row gap-4 items-end">
                    <div className="flex-1 w-full">
                      <label className="block text-xs font-semibold text-gray-500 mb-1">New IP Address</label>
                      <input
                        type="text"
                        placeholder="Ex: 103.25.45.101"
                        value={newIpAddress}
                        onChange={(e) => setNewIpAddress(e.target.value)}
                        className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-[#FBD037] text-sm"
                      />
                    </div>
                    <div className="flex-1 w-full">
                      <label className="block text-xs font-semibold text-gray-500 mb-1">Label</label>
                      <input
                        type="text"
                        placeholder="Ex: Head Office"
                        value={newIpLabel}
                        onChange={(e) => setNewIpLabel(e.target.value)}
                        className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-[#FBD037] text-sm"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={addIpAddress}
                      className="bg-slate-800 text-white text-xs font-semibold px-4 py-2.5 rounded-lg hover:bg-slate-700 transition active:scale-95 flex items-center gap-1 cursor-pointer h-10 w-full md:w-auto justify-center"
                    >
                      <IconPlus size={16} /> Add IP
                    </button>
                  </div>

                </div>
              )}
            </div>

          </div>
        </div>

        {/* WiFi Restrictions */}
        <div className="border border-slate-100 rounded-xl p-5 bg-[#FFFFFD]">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-sm font-semibold text-[#212121] flex items-center gap-2">
              <IconWifi size={18} className="text-[#FBD037]" />
              WiFi Restrictions
            </h3>
            <ToggleButton
              checked={enableWifiRestriction}
              onChange={(e) => setEnableWifiRestriction(e.target.checked)}
            />
          </div>
          
          {enableWifiRestriction && (
            <div className="space-y-4 pt-2 animate-in fade-in duration-200">
              <p className="text-xs text-slate-400">Configure allowed WiFi networks for clock-in (SSID & MAC/BSSID)</p>
              
              {/* Allowed WiFi list */}
              <div className="border border-slate-100 rounded-lg overflow-hidden">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100 font-semibold text-slate-700">
                      <th className="p-3">WiFi Name (SSID)</th>
                      <th className="p-3">BSSID / MAC Address</th>
                      <th className="p-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {wifiNetworks.map((wifiItem, index) => (
                      <tr key={index} className="hover:bg-slate-50/50">
                        <td className="p-3 font-medium text-slate-800">{wifiItem.ssid}</td>
                        <td className="p-3 text-slate-600 font-mono">{wifiItem.bssid}</td>
                        <td className="p-3 text-right">
                          <button
                            type="button"
                            onClick={() => removeWifiNetwork(index)}
                            className="text-red-500 hover:text-red-700 p-1.5 rounded-lg hover:bg-red-50 transition active:scale-95"
                          >
                            <IconTrash size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {wifiNetworks.length === 0 && (
                      <tr>
                        <td colSpan={3} className="p-4 text-center text-slate-400 italic">No WiFi network restrictions configured.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Form to add wifi */}
              <div className="bg-slate-50/50 p-4 rounded-xl border border-slate-100 flex flex-col md:flex-row gap-4 items-end">
                <div className="flex-1 w-full">
                  <label className="block text-xs font-semibold text-gray-500 mb-1">WiFi Name (SSID)</label>
                  <input
                    type="text"
                    placeholder="Ex: Head Office WiFi"
                    value={newWifiSsid}
                    onChange={(e) => setNewWifiSsid(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-[#FBD037] text-sm"
                  />
                </div>
                <div className="flex-1 w-full">
                  <label className="block text-xs font-semibold text-gray-500 mb-1">MAC Address (BSSID) - Optional</label>
                  <input
                    type="text"
                    placeholder="Ex: 90:6C:AC:71:54:FB"
                    value={newWifiBssid}
                    onChange={(e) => setNewWifiBssid(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-[#FBD037] text-sm"
                  />
                </div>
                <button
                  type="button"
                  onClick={addWifiNetwork}
                  className="bg-slate-800 text-white text-xs font-semibold px-4 py-2.5 rounded-lg hover:bg-slate-700 transition active:scale-95 flex items-center gap-1 cursor-pointer h-10 w-full md:w-auto justify-center"
                >
                  <IconPlus size={16} /> Add WiFi
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Schedule & Reminders */}
        <div className="border border-slate-100 rounded-xl p-5 bg-[#FFFFFD]">
          <h3 className="text-sm font-semibold text-[#212121] mb-4 flex items-center gap-2">
            <IconCalendar size={18} className="text-[#FBD037]" />
            Schedule & Reminders
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-end">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                Week Starts From
              </label>
              <select
                value={weekStartsFrom}
                onChange={(e) => setWeekStartsFrom(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-[#FBD037] text-sm font-medium bg-white"
              >
                <option value="MONDAY">Monday</option>
                <option value="TUESDAY">Tuesday</option>
                <option value="WEDNESDAY">Wednesday</option>
                <option value="THURSDAY">Thursday</option>
                <option value="FRIDAY">Friday</option>
                <option value="SATURDAY">Saturday</option>
                <option value="SUNDAY">Sunday</option>
              </select>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <label className="block text-xs font-semibold text-gray-600">
                  Enable Attendance Reminder
                </label>
                <ToggleButton
                  checked={enableAttendanceReminder}
                  onChange={(e) => setEnableAttendanceReminder(e.target.checked)}
                />
              </div>
              {enableAttendanceReminder && (
                <div className="animate-in fade-in duration-200">
                  <label className="block text-xs font-semibold text-gray-400 mb-1.5">
                    Reminder Minutes Before Shift Start
                  </label>
                  <input
                    type="number"
                    value={attendanceReminderMinutesBefore}
                    onChange={(e) => setAttendanceReminderMinutesBefore(parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-[#FBD037] text-sm font-medium"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving || isLoading}
            className="bg-[#FBD037] text-[#212121] text-xs font-semibold px-5 py-2.5 rounded-full hover:bg-[#e2bd30] transition cursor-pointer active:scale-95 disabled:opacity-60"
          >
            {isSaving ? "Saving..." : "Save Settings"}
          </button>
          <button
            type="button"
            onClick={loadSettings}
            disabled={isSaving || isLoading}
            className="border border-[#D2D2D2] text-[#212121] text-xs font-semibold px-5 py-2.5 rounded-full hover:bg-gray-50 transition cursor-pointer disabled:opacity-60 bg-white"
          >
            Cancel
          </button>
        </div>

      </div>
    </div>
  );
}
